import * as React from "react"
import { Outlet, useMatch, useNavigate } from "react-router"

import { AppShell } from "@/components/chat/app-shell"
import { CallBar } from "@/components/chat/voice-panel"
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty"
import { DESKTOP_QUERY, useMediaQuery } from "@/lib/media"
import { RoomsPane } from "@/screens/rooms"

/**
 * Layout for everything behind the sign-in gate.
 *
 * On a phone each route is a full screen and the chat list is one of them.
 * From `md` up the list becomes a fixed sidebar and the routed screen — a
 * thread, its info page, or the placeholder below — fills the rest, so
 * resizing the window between the two never loses the open chat: the URL is
 * the same either way.
 */
export function Workspace() {
  const desktop = useMediaQuery(DESKTOP_QUERY)
  const navigate = useNavigate()
  const activeRoomId = useMatch("/r/:id/*")?.params.id ?? null

  // A tapped notification lands in the room it was about: the worker posts
  // it (see `src/sw.ts`), or, for one the page showed without a worker,
  // `showLocalNotification` dispatches it on the window.
  React.useEffect(() => {
    const open = (roomId: string) =>
      void navigate(`/r/${encodeURIComponent(roomId)}`)

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; roomId?: string } | null
      if (data?.type === "openRoom" && typeof data.roomId === "string") {
        open(data.roomId)
      }
    }
    const onLocal = (event: Event) => {
      const roomId = (event as CustomEvent<unknown>).detail
      if (typeof roomId === "string") open(roomId)
    }

    const worker = navigator.serviceWorker
    worker?.addEventListener("message", onMessage)
    window.addEventListener("chatforge:openRoom", onLocal)
    return () => {
      worker?.removeEventListener("message", onMessage)
      window.removeEventListener("chatforge:openRoom", onLocal)
    }
  }, [navigate])

  if (!desktop) {
    return (
      <AppShell>
        <CallBar activeRoomId={activeRoomId} />
        <Outlet />
      </AppShell>
    )
  }

  return (
    <AppShell wide>
      <CallBar activeRoomId={activeRoomId} />
      <div className="flex min-h-0 flex-1">
        <aside className="flex w-80 flex-none flex-col border-r lg:w-96">
          <RoomsPane />
        </aside>
        <main className="flex min-w-0 flex-1 flex-col">
          <Outlet />
        </main>
      </div>
    </AppShell>
  )
}

/** The `/` route: the list itself on a phone, a placeholder beside it on desktop. */
export function Home() {
  const desktop = useMediaQuery(DESKTOP_QUERY)

  if (!desktop) return <RoomsPane />

  return (
    <Empty className="flex-1">
      <EmptyHeader>
        <EmptyTitle>Выберите чат</EmptyTitle>
        <EmptyDescription>
          Откройте беседу слева или создайте новую.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  )
}
